/**
 * Requirements & Controlled Documents Section Component
 * POV-0091: Section 4 of Part Overview Panel
 * 
 * Per PRD Section 10.4 + Document Expiry Enhancement:
 * - Linked documents table with type, title, revision, status, expiry
 * - Expiry status badges (Expired, Expiring Soon 30d/6m/1y)
 * - Threshold filter dropdown + custom date pickers
 * - Link/Unlink modals with reason-for-change
 * - AS9100D Guidance banner (Clauses 7.5, 8.2.3, 8.4.3)
 * 
 * section_key: "requirements_documents"
 */
import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import {
  FileText,
  Link2,
  Unlink,
  Plus,
  Lock,
  Loader2,
  AlertTriangle,
  ExternalLink,
  Search,
  Info,
  CalendarDays,
  Filter,
  Clock,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ViewRelatedHistoryLink } from './HistorySection';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { toast } from 'sonner';
import api from '../../services/api';

const SECTION_KEY = 'requirements_documents';

const LINK_TYPE_LABELS = {
  DRAWING: 'Drawing',
  SPEC: 'Specification',
  WORK_INSTRUCTION: 'Work Instruction',
  INSPECTION_PLAN: 'Inspection Plan',
  CERTIFICATE: 'Certificate',
  CUSTOMER_REQ: 'Customer Req',
  OTHER: 'Other',
};

const DOC_STATUS_STYLES = {
  Effective: 'bg-emerald-100 text-emerald-700',
  Draft: 'bg-amber-100 text-amber-700',
  Obsolete: 'bg-red-100 text-red-700',
};

const EXPIRY_STATUS_CONFIG = {
  expired: { label: 'Expired', className: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle },
  expiring_30d: { label: '< 30 days', className: 'bg-orange-100 text-orange-800 border-orange-200', icon: Clock },
  expiring_6m: { label: '< 6 months', className: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  expiring_1y: { label: '< 1 year', className: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: CalendarDays },
  active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: null },
  no_expiry: { label: 'No Expiry', className: 'bg-slate-50 text-slate-500 border-slate-200', icon: null },
};

const EXPIRY_FILTER_OPTIONS = [
  { value: 'all', label: 'All Documents' },
  { value: 'expired', label: 'Expired' },
  { value: 'expiring_30d', label: 'Expiring in 30 days' },
  { value: 'expiring_6m', label: 'Expiring in 6 months' },
  { value: 'expiring_1y', label: 'Expiring in 1 year' },
  { value: 'expiring_any', label: 'Any Expiry Warning' },
];

/** DatePicker using Calendar + Popover */
const DatePicker = ({ value, onChange, placeholder = 'Pick a date', id }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('w-full justify-start text-left text-xs font-normal h-8', !value && 'text-slate-400')}
          data-testid={id}
        >
          <CalendarDays className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
          {value ? format(value, 'dd MMM yyyy') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(d) => { onChange(d); setOpen(false); }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

/** Expiry badge */
const ExpiryBadge = ({ status, days }) => {
  const config = EXPIRY_STATUS_CONFIG[status];
  if (!config || status === 'active' || status === 'no_expiry') return null;
  const IconComp = config.icon;
  return (
    <span className={cn('inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border', config.className)} data-testid={`expiry-badge-${status}`}>
      {IconComp && <IconComp className="w-2.5 h-2.5" />}
      {status === 'expired' ? 'Expired' : `${Math.abs(days)}d`}
    </span>
  );
};

/** AS9100D Guidance Banner */
const GuidanceBanner = () => (
  <div className="mb-4 p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs" data-testid="guidance-banner-requirements">
    <div className="flex items-start gap-2">
      <Info className="w-3.5 h-3.5 text-sky-600 mt-0.5 shrink-0" />
      <div>
        <p className="font-semibold text-sky-800">AS9100D Clauses: 7.5, 8.2.3, 8.4.3</p>
        <ul className="mt-1 space-y-0.5 text-sky-700">
          <li>Documented information required for conformity of products and services</li>
          <li>Review requirements for products and services before commitment</li>
          <li>Information for external providers including applicable requirements</li>
        </ul>
      </div>
    </div>
  </div>
);

/** Expiry Summary Banner */
const ExpirySummary = ({ links }) => {
  const expired = links.filter((l) => l.expiry_status === 'expired').length;
  const exp30 = links.filter((l) => l.expiry_status === 'expiring_30d').length;
  const exp6m = links.filter((l) => l.expiry_status === 'expiring_6m').length;
  if (expired === 0 && exp30 === 0 && exp6m === 0) return null;

  return (
    <div className="flex flex-wrap gap-2" data-testid="expiry-summary">
      {expired > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 px-2.5 py-1.5 rounded-md">
          <AlertTriangle className="w-3.5 h-3.5" />
          <span className="font-semibold">{expired}</span> expired
        </div>
      )}
      {exp30 > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-orange-700 bg-orange-50 border border-orange-200 px-2.5 py-1.5 rounded-md">
          <Clock className="w-3.5 h-3.5" />
          <span className="font-semibold">{exp30}</span> expiring within 30 days
        </div>
      )}
      {exp6m > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-md">
          <Clock className="w-3.5 h-3.5" />
          <span className="font-semibold">{exp6m}</span> expiring within 6 months
        </div>
      )}
    </div>
  );
};

/** Link Document Modal */
const LinkDocumentModal = ({ open, onClose, partId, onLinked }) => {
  const [step, setStep] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [linkType, setLinkType] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(null);
  const [effectiveTo, setEffectiveTo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newDocNumber, setNewDocNumber] = useState('');
  const [newDocTitle, setNewDocTitle] = useState('');
  const [newDocRevision, setNewDocRevision] = useState('');
  const [newDocStatus, setNewDocStatus] = useState('Effective');

  const searchDocs = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await api.get(`/api/v1/documents/search?q=${encodeURIComponent(searchQuery)}&limit=20`);
      setSearchResults(res.data);
    } catch { toast.error('Failed to search documents'); }
    finally { setSearching(false); }
  }, [searchQuery]);

  const buildLinkPayload = (docId) => {
    const payload = { document_id: docId, link_type: linkType };
    if (effectiveFrom) payload.effective_from = format(effectiveFrom, 'yyyy-MM-dd');
    if (effectiveTo) payload.effective_to = format(effectiveTo, 'yyyy-MM-dd');
    return payload;
  };

  const handleCreateAndLink = async () => {
    if (!newDocNumber.trim() || !newDocTitle.trim() || !linkType) { toast.error('Document number, title, and link type are required'); return; }
    setSaving(true);
    try {
      const docRes = await api.post('/api/v1/documents', { doc_number: newDocNumber, title: newDocTitle, doc_revision: newDocRevision || null, doc_status: newDocStatus });
      const linkRes = await api.post(`/api/v1/documents/part/${partId}/links`, buildLinkPayload(docRes.data.document_id));
      toast.success(`Linked ${newDocNumber} as ${LINK_TYPE_LABELS[linkType]}`);
      onLinked(linkRes.data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create and link document'); }
    finally { setSaving(false); }
  };

  const handleLinkExisting = async () => {
    if (!selectedDoc || !linkType) { toast.error('Select a document and link type'); return; }
    setSaving(true);
    try {
      const res = await api.post(`/api/v1/documents/part/${partId}/links`, buildLinkPayload(selectedDoc.document_id));
      toast.success(`Linked ${selectedDoc.doc_number} as ${LINK_TYPE_LABELS[linkType]}`);
      onLinked(res.data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to link document'); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    if (!open) { setStep('search'); setSearchQuery(''); setSearchResults([]); setSelectedDoc(null); setLinkType(''); setEffectiveFrom(null); setEffectiveTo(null); setNewDocNumber(''); setNewDocTitle(''); setNewDocRevision(''); setNewDocStatus('Effective'); }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="link-document-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 className="w-4 h-4" /> Link Document</DialogTitle>
          <DialogDescription>Link a controlled document to this part.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-md">
          <button onClick={() => setStep('search')} className={cn('flex-1 text-xs py-1.5 px-3 rounded', step === 'search' ? 'bg-white shadow text-slate-900' : 'text-slate-500')}>Search Existing</button>
          <button onClick={() => setStep('create')} className={cn('flex-1 text-xs py-1.5 px-3 rounded', step === 'create' ? 'bg-white shadow text-slate-900' : 'text-slate-500')}>Create New</button>
        </div>

        {step === 'search' ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input placeholder="Search by doc number or title..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchDocs()} data-testid="doc-search-input" />
              <Button size="sm" variant="outline" onClick={searchDocs} disabled={searching}>
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            {searchResults.length > 0 && (
              <div className="max-h-40 overflow-y-auto border rounded-md divide-y">
                {searchResults.map((doc) => (
                  <button key={doc.document_id} onClick={() => setSelectedDoc(doc)} className={cn('w-full text-left p-2 text-xs hover:bg-slate-50 transition', selectedDoc?.document_id === doc.document_id && 'bg-sky-50 border-l-2 border-sky-500')} data-testid={`doc-result-${doc.doc_number}`}>
                    <span className="font-mono font-medium">{doc.doc_number}</span>
                    <span className="text-slate-500 ml-2">{doc.title}</span>
                    <span className={cn('ml-2 px-1.5 py-0.5 rounded text-[10px]', DOC_STATUS_STYLES[doc.doc_status])}>{doc.doc_status}</span>
                  </button>
                ))}
              </div>
            )}
            {selectedDoc && (
              <div className="p-2 bg-sky-50 rounded text-xs">Selected: <span className="font-semibold">{selectedDoc.doc_number}</span> — {selectedDoc.title}</div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div><Label className="text-xs">Document Number *</Label><Input value={newDocNumber} onChange={(e) => setNewDocNumber(e.target.value)} placeholder="DWG-12345" data-testid="new-doc-number" /></div>
            <div><Label className="text-xs">Title *</Label><Input value={newDocTitle} onChange={(e) => setNewDocTitle(e.target.value)} placeholder="PLC Assembly Drawing" data-testid="new-doc-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Revision</Label><Input value={newDocRevision} onChange={(e) => setNewDocRevision(e.target.value)} placeholder="Rev A" /></div>
              <div><Label className="text-xs">Status</Label>
                <Select value={newDocStatus} onValueChange={setNewDocStatus}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Draft">Draft</SelectItem><SelectItem value="Effective">Effective</SelectItem><SelectItem value="Obsolete">Obsolete</SelectItem></SelectContent></Select>
              </div>
            </div>
          </div>
        )}

        {/* Link Type + Effective Dates (shared) */}
        <div className="space-y-3 pt-2 border-t">
          <div>
            <Label className="text-xs">Link Type *</Label>
            <Select value={linkType} onValueChange={setLinkType}><SelectTrigger data-testid="link-type-select"><SelectValue placeholder="Select type..." /></SelectTrigger><SelectContent>{Object.entries(LINK_TYPE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent></Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Effective From</Label>
              <DatePicker value={effectiveFrom} onChange={setEffectiveFrom} placeholder="Start date" id="effective-from-picker" />
            </div>
            <div>
              <Label className="text-xs">Effective To</Label>
              <DatePicker value={effectiveTo} onChange={setEffectiveTo} placeholder="Expiry date" id="effective-to-picker" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={step === 'search' ? handleLinkExisting : handleCreateAndLink} disabled={saving || (step === 'search' ? !selectedDoc || !linkType : !newDocNumber || !newDocTitle || !linkType)} data-testid="link-document-confirm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link2 className="w-4 h-4 mr-1" />}
            Link Document
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Unlink Document Modal (Controlled — reason required) */
const UnlinkDocumentModal = ({ open, onClose, link, partId, onUnlinked, reasonCodes }) => {
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [saving, setSaving] = useState(false);

  const handleUnlink = async () => {
    if (!reasonCode) { toast.error('Reason is required'); return; }
    if (reasonCode === 'other' && !reasonText.trim()) { toast.error('Please provide reason text'); return; }
    setSaving(true);
    try {
      await api.delete(`/api/v1/documents/part/${partId}/links/${link.link_id}`, { data: { reason_code: reasonCode, reason_text: reasonText || null } });
      toast.success(`Unlinked ${link.doc_number || 'document'}`);
      onUnlinked(link.link_id);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to unlink'); }
    finally { setSaving(false); }
  };

  useEffect(() => { if (!open) { setReasonCode(''); setReasonText(''); } }, [open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="unlink-document-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700"><Unlink className="w-4 h-4" /> Unlink Document</DialogTitle>
          <DialogDescription>This is a controlled action. You must provide a reason.</DialogDescription>
        </DialogHeader>
        <div className="text-xs p-2 bg-slate-50 rounded"><span className="font-mono font-semibold">{link?.doc_number}</span> — {link?.title}</div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs flex items-center gap-1"><Lock className="w-3 h-3 text-amber-600" /> Reason for Change *</Label>
            <Select value={reasonCode} onValueChange={setReasonCode}><SelectTrigger data-testid="unlink-reason-select"><SelectValue placeholder="Select reason..." /></SelectTrigger><SelectContent>{(reasonCodes || []).map((rc) => (<SelectItem key={rc.code} value={rc.code}>{rc.label}</SelectItem>))}</SelectContent></Select>
          </div>
          {reasonCode === 'other' && (
            <div><Label className="text-xs">Reason Text *</Label><Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="Describe reason..." rows={2} /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" size="sm" onClick={handleUnlink} disabled={saving || !reasonCode} data-testid="unlink-confirm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Unlink className="w-4 h-4 mr-1" />} Unlink
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Main Section Component */
const RequirementsDocumentsSection = ({ partId, onViewRelatedHistory }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState(null);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [expiryFilter, setExpiryFilter] = useState('all');

  const loadLinks = useCallback(async () => {
    if (!partId) return;
    setLoading(true);
    setError(null);
    try {
      const [linksRes, cfRes] = await Promise.all([
        api.get(`/api/v1/documents/part/${partId}/links`),
        api.get('/api/v1/documents/controlled-fields'),
      ]);
      setLinks(linksRes.data);
      setReasonCodes(cfRes.data.reason_codes || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load documents');
    } finally { setLoading(false); }
  }, [partId]);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  const handleLinked = (newLink) => setLinks((prev) => [newLink, ...prev]);
  const handleUnlinked = (linkId) => {
    setLinks((prev) => prev.filter((l) => l.link_id !== linkId));
    if (selectedLink?.link_id === linkId) setSelectedLink(null);
  };

  // Apply client-side filter
  const filteredLinks = expiryFilter === 'all'
    ? links
    : expiryFilter === 'expiring_any'
      ? links.filter((l) => ['expired', 'expiring_30d', 'expiring_6m', 'expiring_1y'].includes(l.expiry_status))
      : links.filter((l) => l.expiry_status === expiryFilter);

  const draftCount = links.filter((l) => l.doc_status === 'Draft').length;
  const obsoleteCount = links.filter((l) => l.doc_status === 'Obsolete').length;

  if (loading) {
    return (<div className="space-y-3 animate-pulse"><div className="h-16 bg-slate-100 rounded" /><div className="h-32 bg-slate-100 rounded" /></div>);
  }

  if (error) {
    return (<div className="text-center py-6"><AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-2" /><p className="text-sm text-red-600">{error}</p><Button variant="outline" size="sm" className="mt-2" onClick={loadLinks}>Retry</Button></div>);
  }

  return (
    <div className="space-y-4" data-testid="requirements-documents-section">
      <GuidanceBanner />

      {/* Expiry Summary */}
      <ExpirySummary links={links} />

      {/* Header with filter + Link action */}
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-slate-700 shrink-0">Linked Documents</h4>
        <div className="flex items-center gap-2">
          <Select value={expiryFilter} onValueChange={setExpiryFilter}>
            <SelectTrigger className="h-7 text-[10px] w-[150px]" data-testid="expiry-filter-select">
              <Filter className="w-3 h-3 mr-1 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPIRY_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowLinkModal(true)} data-testid="link-document-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Link Doc
          </Button>
        </div>
      </div>

      {/* Doc status warnings */}
      {draftCount > 0 && (<div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 p-2 rounded"><AlertTriangle className="w-3.5 h-3.5" />{draftCount} document{draftCount > 1 ? 's' : ''} in Draft status</div>)}
      {obsoleteCount > 0 && (<div className="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 p-2 rounded"><AlertTriangle className="w-3.5 h-3.5" />{obsoleteCount} document{obsoleteCount > 1 ? 's' : ''} Obsolete</div>)}

      {/* Documents Table */}
      {filteredLinks.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm" data-testid="no-documents">
          <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
          {expiryFilter !== 'all' ? 'No documents match the selected filter' : 'No documents linked to this part'}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs w-24">Type</TableHead>
                <TableHead className="text-xs">Document</TableHead>
                <TableHead className="text-xs w-14">Rev</TableHead>
                <TableHead className="text-xs w-20">Status</TableHead>
                <TableHead className="text-xs w-24">Expiry</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLinks.map((link) => (
                <TableRow
                  key={link.link_id}
                  onClick={() => setSelectedLink(link)}
                  className={cn('cursor-pointer text-xs transition-colors', selectedLink?.link_id === link.link_id ? 'bg-sky-50' : 'hover:bg-slate-50')}
                  data-testid={`doc-link-row-${link.link_id}`}
                >
                  <TableCell className="text-xs">{LINK_TYPE_LABELS[link.link_type] || link.link_type}</TableCell>
                  <TableCell>
                    <span className="font-mono font-medium text-xs">{link.doc_number || '—'}</span>
                    <span className="text-slate-500 ml-1.5 text-xs truncate">{link.title || ''}</span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{link.doc_revision || '—'}</TableCell>
                  <TableCell>
                    <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', DOC_STATUS_STYLES[link.doc_status] || 'bg-slate-100 text-slate-600')}>{link.doc_status || '—'}</span>
                  </TableCell>
                  <TableCell>
                    {link.effective_to ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] text-slate-500 font-mono">{link.effective_to}</span>
                        <ExpiryBadge status={link.expiry_status} days={link.days_until_expiry} />
                      </div>
                    ) : (
                      <span className="text-[10px] text-slate-400">No expiry</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Selected Document Detail */}
      {selectedLink && (
        <div className="border rounded-lg p-3 bg-slate-50 space-y-2" data-testid="doc-detail-panel">
          <h5 className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Document Details</h5>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            <div><span className="text-slate-500">Title:</span> <span className="font-medium">{selectedLink.title || '—'}</span></div>
            <div><span className="text-slate-500">Doc Number:</span> <span className="font-mono">{selectedLink.doc_number || '—'}</span></div>
            <div><span className="text-slate-500">Revision:</span> <span className="font-mono">{selectedLink.doc_revision || '—'}</span></div>
            <div><span className="text-slate-500">Link Type:</span> {LINK_TYPE_LABELS[selectedLink.link_type]}</div>
            <div><span className="text-slate-500">Applies To:</span> {selectedLink.part_revision_id ? `Revision ${selectedLink.part_revision_id}` : 'All Revisions'}</div>
            <div><span className="text-slate-500">Effective From:</span> {selectedLink.effective_from || '—'}</div>
            <div><span className="text-slate-500">Effective To:</span> {selectedLink.effective_to || '—'}</div>
            <div>
              <span className="text-slate-500">Expiry Status:</span>{' '}
              {selectedLink.expiry_status === 'no_expiry' ? <span className="text-slate-400">No expiry set</span> :
               selectedLink.expiry_status === 'expired' ? <span className="text-red-600 font-semibold">Expired ({Math.abs(selectedLink.days_until_expiry)} days ago)</span> :
               selectedLink.expiry_status === 'active' ? <span className="text-emerald-600">{selectedLink.days_until_expiry} days remaining</span> :
               <span className="text-amber-600 font-medium">{selectedLink.days_until_expiry} days remaining</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
            {selectedLink.uri && (
              <Button size="sm" variant="outline" className="h-6 text-[10px]" asChild>
                <a href={selectedLink.uri} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-3 h-3 mr-1" /> View Document</a>
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-6 text-[10px] text-red-600 border-red-200 hover:bg-red-50" onClick={() => setUnlinkTarget(selectedLink)} data-testid="unlink-btn">
              <Lock className="w-3 h-3 mr-1" /> Unlink
            </Button>
          </div>
        </div>
      )}

      <ViewRelatedHistoryLink sectionKey={SECTION_KEY} onClick={onViewRelatedHistory} />

      <LinkDocumentModal open={showLinkModal} onClose={() => setShowLinkModal(false)} partId={partId} onLinked={handleLinked} />
      <UnlinkDocumentModal open={!!unlinkTarget} onClose={() => setUnlinkTarget(null)} link={unlinkTarget} partId={partId} onUnlinked={handleUnlinked} reasonCodes={reasonCodes} />
    </div>
  );
};

export default RequirementsDocumentsSection;
